<script setup lang="ts">
/**
 * The framed KPI tile: a quiet top label, one large figure (default slot) and
 * an optional muted line under it (`additionalTexts`, joined with „·“). The
 * framed counterpart of NxMetricGrid — one card per figure, for a row of
 * tiles that filter, link or carry an action.
 *
 * Four slots, all empty by default: `top-start` (a marker in front of the
 * label, e.g. a step number), default (the figure), `top-end` (a badge on the
 * trailing edge) and `footer` (an action under the figure — rendered only when
 * filled, so an empty wrapper never adds height).
 *
 * `tone` colours the figure and draws an accent on the leading edge (overdue
 * in `danger`, target met in `success`); `hue` does the same for a category
 * and wins over `tone`, and `accent` takes any colour token (a document's
 * choice, e.g. a presentation layer) over both. `accentLevel` sets how much
 * of it shows (`NxAccentLevel`: `edge` figure, wash and strip — the default;
 * `tint` no strip; `fill` also a wash over the card; `muted` no colour, the
 * card steps back), `accentEmphasis` the strip's width, `opacity` fades the
 * card. `clickable` makes the whole card one button (click,
 * Enter, Space → `click`) with `active` as its pressed state — leave `footer`
 * empty then, a button in a button is not operable.
 */
import { computed, useSlots } from 'vue';
import { hueVars, type NxHue } from '../lib/hue';
import {
  NX_ACCENT_LEVELS,
  nxtColorTokenVar,
  opacityStyle,
  type NxAccentLevel,
} from '../Panel/accent';

export type NxKpiCardTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const props = withDefaults(
  defineProps<{
    topText: string;
    additionalTexts?: string[];
    tone?: NxKpiCardTone;
    hue?: NxHue;
    /** Any colour token, named without `--nxt-` (`'warning-bg'`); wins over `hue` and `tone`. */
    accent?: string | null;
    accentLevel?: NxAccentLevel;
    /** 0–1: the strip's width (0 = none) and the wash's strength. Default 1. */
    accentEmphasis?: number;
    /** 0–1: fades the whole card. */
    opacity?: number;
    clickable?: boolean;
    active?: boolean;
  }>(),
  {
    additionalTexts: undefined,
    tone: 'neutral',
    hue: undefined,
    accent: undefined,
    accentLevel: 'edge',
    accentEmphasis: 1,
    opacity: 1,
    clickable: false,
    active: false,
  }
);

const emit = defineEmits<{ click: [event: MouseEvent | KeyboardEvent] }>();
const slots = useSlots();

const level = computed<NxAccentLevel>(() =>
  NX_ACCENT_LEVELS.includes(props.accentLevel) ? props.accentLevel : 'edge'
);
const tokenColor = computed(() =>
  level.value === 'muted' ? null : nxtColorTokenVar(props.accent)
);
const colorClass = computed(() => {
  if (level.value === 'muted') return 'is-muted';
  if (tokenColor.value) return 'has-token';
  return props.hue ? 'has-hue' : `tone-${props.tone}`;
});
const emphasis = computed(() =>
  Number.isFinite(props.accentEmphasis) ? Math.min(1, Math.max(0, props.accentEmphasis)) : 1
);

const cardStyle = computed(() => {
  let color: Record<string, string> = {};
  if (tokenColor.value) {
    color = {
      '--kpi-card-accent': tokenColor.value,
      '--kpi-card-figure': `color-mix(in oklab, ${tokenColor.value} 80%, var(--nxt-fg))`,
    };
  } else if (props.hue && level.value !== 'muted') {
    color = {
      ...hueVars(props.hue),
      '--kpi-card-accent': 'var(--hue)',
      '--kpi-card-figure': 'var(--hue-fg)',
    };
  }
  const style = {
    ...color,
    ...(emphasis.value === 1 ? {} : { '--kpi-card-emphasis': String(emphasis.value) }),
    ...opacityStyle(props.opacity),
  };
  return Object.keys(style).length ? style : undefined;
});

function onClick(event: MouseEvent) {
  if (props.clickable) emit('click', event);
}

function onKeydown(event: KeyboardEvent) {
  if (!props.clickable || (event.key !== 'Enter' && event.key !== ' ')) return;
  event.preventDefault();
  emit('click', event);
}
</script>

<template>
  <div
    class="nx-kpi-card"
    :class="[
      colorClass,
      `accent-${level}`,
      { 'is-clickable': props.clickable, 'is-active': props.active },
    ]"
    :style="cardStyle"
    :role="props.clickable ? 'button' : undefined"
    :tabindex="props.clickable ? 0 : undefined"
    :aria-pressed="props.clickable ? props.active : undefined"
    data-testid="nx-kpi-card"
    @click="onClick"
    @keydown="onKeydown"
  >
    <div class="nx-kpi-card__top-row">
      <slot name="top-start" />
      <span class="nx-kpi-card__top">{{ props.topText }}</span>
      <slot name="top-end" />
    </div>
    <div class="nx-kpi-card__figure">
      <slot />
    </div>
    <div v-if="props.additionalTexts?.length" class="nx-kpi-card__additional">
      {{ props.additionalTexts.join(' · ') }}
    </div>
    <div v-if="slots.footer" class="nx-kpi-card__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<style scoped>
.nx-kpi-card {
  --kpi-card-accent: transparent;
  --kpi-card-figure: var(--nxt-fg);
  --kpi-card-emphasis: 1;
  /* The strip on the leading edge, as wide as the emphasis; none at `tint`. */
  --kpi-card-strip: calc(var(--nxt-hairline) * 3 * var(--kpi-card-emphasis));

  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  padding: var(--nxt-space-md);
  overflow: hidden;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-bg);
  box-shadow:
    inset var(--kpi-card-strip) 0 0 var(--kpi-card-accent),
    var(--nxt-shadow-sm);
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out),
    transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
/* A soft wash of the accent in the top corner — reads as colour, not as ink. */
.nx-kpi-card::before {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at 100% 0,
    color-mix(in srgb, var(--kpi-card-accent) 12%, transparent),
    transparent 60%
  );
  content: '';
  pointer-events: none;
}
.nx-kpi-card.tone-info {
  --kpi-card-accent: var(--nxt-info-bg);
  --kpi-card-figure: var(--nxt-info-subtle-fg);
}
.nx-kpi-card.tone-success {
  --kpi-card-accent: var(--nxt-success-bg);
  --kpi-card-figure: var(--nxt-success-subtle-fg);
}
.nx-kpi-card.tone-warning {
  --kpi-card-accent: var(--nxt-warning-bg);
  --kpi-card-figure: var(--nxt-warning-subtle-fg);
}
.nx-kpi-card.tone-danger {
  --kpi-card-accent: var(--nxt-danger-bg);
  --kpi-card-figure: var(--nxt-danger-subtle-fg);
}
.nx-kpi-card.accent-tint {
  --kpi-card-strip: 0;
}
.nx-kpi-card.accent-fill:not(.tone-neutral) {
  background: color-mix(
    in oklab,
    var(--kpi-card-accent) calc(3% + 5% * var(--kpi-card-emphasis)),
    var(--nxt-surface-bg)
  );
}
.nx-kpi-card.is-muted {
  --kpi-card-figure: var(--nxt-fg-muted);
  color: var(--nxt-fg-muted);
  filter: saturate(0.7);
}
.nx-kpi-card.is-clickable {
  cursor: pointer;
  outline: none;
}
.nx-kpi-card.is-clickable:hover {
  border-color: var(--nxt-border-strong);
  box-shadow:
    inset var(--kpi-card-strip) 0 0 var(--kpi-card-accent),
    var(--nxt-shadow-md);
  transform: translateY(calc(var(--nxt-hairline) * -1));
}
.nx-kpi-card.is-clickable:focus-visible {
  box-shadow:
    inset var(--kpi-card-strip) 0 0 var(--kpi-card-accent),
    0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-kpi-card.is-active {
  border-color: var(--nxt-brand-border);
  background-color: var(--nxt-brand-subtle-bg);
}
.nx-kpi-card__top-row {
  position: relative;
  display: flex;
  align-self: stretch;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
/* Shrinks below its text and ellipses, so a long label never runs under the
   `top-end` badge. */
.nx-kpi-card__top {
  min-width: 0;
  margin-inline-end: auto;
  overflow: hidden;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  letter-spacing: 0.02em;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}
.nx-kpi-card__figure {
  position: relative;
  color: var(--kpi-card-figure);
  font-size: var(--nxt-font-size-2xl);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: -0.025em;
  line-height: var(--nxt-line-height-tight);
}
.nx-kpi-card__additional {
  position: relative;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.nx-kpi-card__footer {
  position: relative;
  align-self: stretch;
  margin-block-start: var(--nxt-space-sm);
}
@media (prefers-reduced-motion: reduce) {
  .nx-kpi-card {
    transition: none;
  }
  .nx-kpi-card.is-clickable:hover {
    transform: none;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-kpi-card {
  transition: none;
}
:where([data-nx-motion='reduced']) .nx-kpi-card.is-clickable:hover {
  transform: none;
}
</style>
